// @givmo/passkeys — how a company sign-in's answer comes back to the page that started it (AS-WEB30).
//
// The ceremony leaves the page: the browser goes to the employer's own sign-in page, and the backend
// sends it back to this console's `/sso/callback` with a one-use handoff code (or an error). Two ways
// back, popup first (the web's house strategy for Google and Microsoft, `@givmo/auth` `strategy.ts`):
//
//   1. POPUP — the page that started keeps its tab and its PKCE verifier in memory; the callback page
//      opens in the popup, hands the answer back here and closes. It hands it back TWO ways, because
//      either can be missing: `window.opener.postMessage` (to this origin only), and a same-origin
//      `BroadcastChannel`. The second is not decoration: an identity provider's page that sends a
//      strict Cross-Origin-Opener-Policy moves the popup into its own browsing-context group, and
//      the popup's `window.opener` is gone for good, even once it is back on this origin. A
//      BroadcastChannel reaches every same-origin page in the browser profile regardless.
//
//      ⚠ SO ONE ANSWER USUALLY ARRIVES TWICE, and the handoff it carries is single-use. Each answer
//      the callback page publishes carries one id on both of its copies, and the listener hands an
//      id over once: the first channel to deliver wins, the other copy is dropped. (Before this, the
//      page that started exchanged the handoff twice: one 400 `sso_handoff_rejected` per sign-in, a
//      false "didn't finish" while the winner finished, and the token route's per-IP allowance spent
//      twice — cut 1007e's review, F2.)
//   2. SAME TAB — a blocked popup sends this tab itself. The verifier, the typed address and where to
//      land go in `sessionStorage` (per tab, per origin) for the callback page to finish with.
//
// No Firebase handshake is involved in either, so the same-tab path needs no same-site authDomain.
//
// A same-tab return that cannot finish (a refusal, or an attempt older than the backend's flow) goes
// to the console's own step page, carrying the typed address and the reason in router history state
// (`ORGANIZATION_SSO_STATE_KEY`), never in the address bar.

import type { OrganizationSsoFailure } from './organization-sso-transport';

/** The message type the callback page posts, and the channel it posts on. */
export const ORGANIZATION_SSO_RESULT = 'givmo.organization-sso.result';
const CHANNEL_NAME = 'givmo.organization-sso';

/** What the callback page carries back: a handoff code, or the backend's error code. */
export interface OrganizationSsoResult {
  handoffCode: string | null;
  error: string | null;
}

/** One published answer, as it arrives: the result, and the id both of its copies carry. */
interface PublishedAnswer {
  result: OrganizationSsoResult;
  /** The publication's id; `null` from a page that sent none (each copy is then its own answer). */
  id: string | null;
}

function answerOf(data: unknown): PublishedAnswer | null {
  if (typeof data !== 'object' || data === null) return null;
  const { type, id, handoffCode, error } = data as {
    type?: unknown;
    id?: unknown;
    handoffCode?: unknown;
    error?: unknown;
  };
  if (type !== ORGANIZATION_SSO_RESULT) return null;
  const code = typeof handoffCode === 'string' && handoffCode.length > 0 ? handoffCode : null;
  const why = typeof error === 'string' && error.length > 0 ? error : null;
  if (code === null && why === null) return null;
  return {
    result: { handoffCode: code, error: why },
    id: typeof id === 'string' && id.length > 0 ? id : null,
  };
}

/** A fresh id for one publication (both of its copies carry it). */
function newAnswerId(): string {
  try {
    const uuid = globalThis.crypto?.randomUUID?.();
    if (typeof uuid === 'string') return uuid;
  } catch {
    // Not a secure context: the fallback below is only an identity, never a secret.
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

/**
 * The callback page, in a popup: hand the answer to the page that opened it. Returns whether it had
 * anywhere to send it (an opener on this origin, or a BroadcastChannel).
 */
export function publishOrganizationSsoResult(result: OrganizationSsoResult): boolean {
  // ONE id for both copies, so the page that started hands this answer over once (the header).
  const message = { type: ORGANIZATION_SSO_RESULT, id: newAnswerId(), ...result };
  let sent = false;
  try {
    const opener = globalThis.opener as Window | null | undefined;
    if (opener !== null && opener !== undefined && opener !== globalThis.window) {
      opener.postMessage(message, globalThis.location.origin);
      sent = true;
    }
  } catch {
    // A cross-origin or closed opener throws on access: the channel below still reaches it.
  }
  try {
    if (typeof BroadcastChannel !== 'undefined') {
      const channel = new BroadcastChannel(CHANNEL_NAME);
      channel.postMessage(message);
      channel.close();
      sent = true;
    }
  } catch {
    // No channel in this browser: the opener above is the only way back.
  }
  return sent;
}

/**
 * The page that started the ceremony: listen for the callback page's answer, from this origin only,
 * and hand each published answer over ONCE, whichever of its two channels delivers it first (the
 * header). Returns the unsubscribe.
 */
export function listenForOrganizationSsoResult(
  onResult: (result: OrganizationSsoResult) => void,
): () => void {
  const handedOver = new Set<string>();
  const deliver = (data: unknown) => {
    const answer = answerOf(data);
    if (answer === null) return;
    if (answer.id !== null) {
      if (handedOver.has(answer.id)) return;
      handedOver.add(answer.id);
    }
    onResult(answer.result);
  };
  const onMessage = (event: MessageEvent) => {
    if (event.origin !== globalThis.location.origin) return;
    deliver(event.data);
  };
  globalThis.addEventListener('message', onMessage);
  let channel: BroadcastChannel | null = null;
  try {
    if (typeof BroadcastChannel !== 'undefined') {
      channel = new BroadcastChannel(CHANNEL_NAME);
      channel.onmessage = (event: MessageEvent) => deliver(event.data);
    }
  } catch {
    channel = null;
  }
  return () => {
    globalThis.removeEventListener('message', onMessage);
    channel?.close();
  };
}

// ── The same-tab path's attempt ────────────────────────────────────────────────────────────────

const ATTEMPT_STORAGE_ITEM = 'givmo.organization-sso-attempt.v1';

/** The backend's flow life: an attempt older than this can no longer be exchanged. */
const ATTEMPT_LIFE_MS = 15 * 60 * 1000;

/** What the callback page needs to finish a same-tab sign-in. */
export interface OrganizationSsoAttempt {
  codeVerifier: string;
  email: string;
  /** Where to land once signed in (a sanitized same-origin path). */
  returnTo: string;
  /** Which register the step was in, so a failure reopens it in the same one. */
  register: 'console' | 'director';
  /**
   * Where the step's "Back to sign in" went (a sanitized same-origin path), so a return that could
   * not finish reopens the step with the same way back (cut 1007e's review, F5). Null: the console's
   * own door.
   */
  back?: string | null;
}

/** Keep the attempt for this tab's return. Returns false where storage is unusable. */
export function saveOrganizationSsoAttempt(attempt: OrganizationSsoAttempt): boolean {
  try {
    const store = globalThis.sessionStorage;
    if (store === undefined) return false;
    store.setItem(ATTEMPT_STORAGE_ITEM, JSON.stringify({ ...attempt, startedAt: Date.now() }));
    return true;
  } catch {
    return false;
  }
}

/** What this tab kept for its return. */
export interface OrganizationSsoAttemptRead {
  attempt: OrganizationSsoAttempt;
  /**
   * Older than the backend's flow: its verifier can no longer be exchanged. Still returned, because
   * it says this tab went to the company's page itself (a popup never holds an attempt: one left
   * behind is cleared before a window opens), and where the step was and what was typed.
   */
  expired: boolean;
}

/** The attempt this tab started, consumed on read; null if none or unreadable. */
export function takeOrganizationSsoAttempt(): OrganizationSsoAttemptRead | null {
  try {
    const store = globalThis.sessionStorage;
    if (store === undefined) return null;
    const raw = store.getItem(ATTEMPT_STORAGE_ITEM);
    store.removeItem(ATTEMPT_STORAGE_ITEM);
    if (typeof raw !== 'string') return null;
    const parsed = JSON.parse(raw) as Partial<OrganizationSsoAttempt & { startedAt: number }>;
    if (
      typeof parsed.codeVerifier !== 'string' ||
      typeof parsed.email !== 'string' ||
      typeof parsed.returnTo !== 'string' ||
      typeof parsed.startedAt !== 'number'
    ) {
      return null;
    }
    return {
      attempt: {
        codeVerifier: parsed.codeVerifier,
        email: parsed.email,
        returnTo: parsed.returnTo,
        register: parsed.register === 'director' ? 'director' : 'console',
        back: typeof parsed.back === 'string' && parsed.back.length > 0 ? parsed.back : null,
      },
      expired: Date.now() - parsed.startedAt > ATTEMPT_LIFE_MS,
    };
  } catch {
    return null;
  }
}

/**
 * The register of the attempt this tab kept, WITHOUT taking it: the callback page draws its own
 * frame in the step's register (Board's shell for a director) before it finishes the attempt (cut
 * 1007e's fold, Board's B-F3). Null when there is none (the company's window, a bare visit).
 */
export function keptOrganizationSsoAttemptRegister(): OrganizationSsoAttempt['register'] | null {
  try {
    const raw = globalThis.sessionStorage?.getItem(ATTEMPT_STORAGE_ITEM);
    if (typeof raw !== 'string') return null;
    const parsed = JSON.parse(raw) as { register?: unknown };
    return parsed.register === 'director' ? 'director' : 'console';
  } catch {
    return null;
  }
}

// ── What a return that could not finish hands its step page ─────────────────────────────────────

/** The history-state key a console carries {@link OrganizationSsoReturnState} under. */
export const ORGANIZATION_SSO_STATE_KEY = 'organizationSso';

/** The address the person typed and why company sign-in didn't finish (`null`: a fresh step). */
export interface OrganizationSsoReturnState {
  email: string;
  failure: OrganizationSsoFailure | null;
}

const FAILURES: ReadonlySet<string> = new Set<OrganizationSsoFailure>([
  'noSso',
  'didNotFinish',
  'notLinked',
  'notHere',
  'throttled',
]);

/** A location's carried return, read tolerantly: `null` when absent or malformed (a reload, a typed address). */
export function organizationSsoReturnOf(state: unknown): OrganizationSsoReturnState | null {
  if (typeof state !== 'object' || state === null) return null;
  const carried = (state as Record<string, unknown>)[ORGANIZATION_SSO_STATE_KEY];
  if (typeof carried !== 'object' || carried === null) return null;
  const { email, failure } = carried as { email?: unknown; failure?: unknown };
  return {
    email: typeof email === 'string' ? email : '',
    failure:
      typeof failure === 'string' && FAILURES.has(failure)
        ? (failure as OrganizationSsoFailure)
        : null,
  };
}
